import { ActivatedRoute } from '@angular/router';
import {
	AfterViewChecked,
	Component,
	ElementRef,
	HostListener,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChild,
} from '@angular/core';
import {
	UntypedFormGroup,
	UntypedFormControl,
	Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import _ from 'lodash-es';
import { CompanyUpdateConfirmationComponent } from './components/company-update-confirmation/company-update-confirmation.component.js';
import { EditItemComponent } from './components/edit-item/edit-item.component.js';
import { NotificationsComponent } from './components/notifications/notifications.component.js';
import { ReviewEditsService } from './services/review-edits.service.js';
import { CompanyUpdateService } from './services/company-update.service.js';
import { compareCompanies, Diff, itemIsDifferent } from './utils/index.js';

import {
	SelfUpdateMode,
	SubmittedUpdate,
	ItemReviewModification,
} from './company-update.interface.js';
import {
	Company,
	CompanyKey,
	EditItemType,
	getDependentCompanyFields,
	getUpdateableCompanyFields,
	getCompanyUpdateOptions,
	isBlueKnightProperty,
	isCompanyContactTitle,
	LocalizedTextIds,
	nameof,
	TabConfig,
	UpdateStatus,
	urlIdentifierForCompany,
	Mode,
	hasRequiredChanges,
} from 'company-finder-common';
import {
	BreadcrumbsService,
	CompanyService,
	ComponentOkToDeactivate,
	DeploymentContext,
	requireIfBlueKnightCompany,
	RequireMoreThanJustSpaces,
	SimpleTabUIComponent,
	StickyOnScrollComponent,
	TabInfo,
	TitleAndMetadata,
	UserService,
	WebAnalyticsService,
} from '@Common';
import { Subscription } from 'rxjs';
import { JnJInformationUpdateComponent } from './components/jnj-information-update/jnj-information-update.component.js';
import { BlueKnightInformationUpdateComponent } from './components/blue-knight-information-update/blue-knight-information-update.component.js';
import { BlueKnightMilestonesUpdateComponent } from './components/blue-knight-milestones-update/blue-knight-milestones-update.component.js';
import { PublicInformationUpdateComponent } from './components/public-information-update/public-information-update.component.js';
import { UpdateComponentBaseWithEditItems } from './UpdateComponentBaseWithEditItems.js';
import { UpdateHeaderComponent } from './components/header/update-header.component.js';
import { Observable } from 'rxjs';
import { AssetsUpdateComponent } from './components/assets-update/assets-update.component.js';

@Component({
	selector: 'company-update',
	templateUrl: './company-update.component.html',
	styleUrls: ['./company-update.component.scss'],
	standalone: false,
})
export class CompanyUpdateComponent
	extends UpdateComponentBaseWithEditItems
	implements AfterViewChecked, OnDestroy, OnInit, ComponentOkToDeactivate
{
	public isShowingTooltip = {
		primary: false,
		secondary: false,
	};

	@ViewChild('notificationsComponent', { static: true })
	public notificationsComponent: NotificationsComponent;
	@ViewChild(UpdateHeaderComponent)
	public header: UpdateHeaderComponent;
	@ViewChild('submissionConfirmation', { static: true })
	public submissionConfirmation: CompanyUpdateConfirmationComponent;
	public parentForm: UntypedFormGroup;
	public companyEditFormSubscription: Subscription;

	@HostListener('window:beforeunload')
	okToDeactivate(): Observable<boolean> | boolean {
		if (this._deploymentContext.debug.allowAllReloads) {
			return true;
		}
		return this.reviewSubmitted || !this.hasChanges();
	}

	public tabs: TabInfo[] = [
		{
			highlightWidth: 77,
			label: this.Loc(LocalizedTextIds.CompanyDetailsPublicInfo),
			value: TabConfig.PUBLIC,
			getErrorInfo: (): string[] =>
				this.getErrorsForTab(
					nameof<CompanyUpdateComponent>('publicInformation')
				),
		},
	];

	public assetsTab: TabInfo = {
		highlightWidth: 77,
		label: this.Loc(LocalizedTextIds.Assets),
		value: TabConfig.ASSETS,
		getErrorInfo: (): string[] =>
			this.getErrorsForTab(nameof<CompanyUpdateComponent>('assets')),
	};

	public internalTab: TabInfo = {
		highlightWidth: 145,
		label: this.Loc(LocalizedTextIds.CompanyDetailsInternalJJInfo),
		lockIcon: true,
		value: TabConfig.JNJ_ONLY,
		getErrorInfo: (): string[] =>
			this.getErrorsForTab(
				nameof<CompanyUpdateComponent>('jnjInformation')
			),
	};

	public blueKnightTab: TabInfo = {
		blueKnightIcon: true,
		highlightWidth: 242,
		label: this.Loc(LocalizedTextIds.CompanyDetailsInternalBKOverview),
		lockIcon: true,
		value: TabConfig.BLUE_KNIGHT,
		getErrorInfo: (): string[] =>
			this.getErrorsForTab(
				nameof<CompanyUpdateComponent>('blueKnightInformation')
			),
	};

	public blueKnightMilestonesTab: TabInfo = {
		blueKnightIcon: true,
		highlightWidth: 252,
		label: this.Loc(LocalizedTextIds.CompanyDetailsInternalBKMilestones),
		lockIcon: true,
		value: TabConfig.BLUE_KNIGHT_MILESTONES,
		getErrorInfo: (): string[] =>
			this.getErrorsForTab(
				nameof<CompanyUpdateComponent>('blueKnightMilestones')
			),
	};

	@ViewChild('editsSubmissionSummary', { static: true })
	public editsSubmissionSummary: StickyOnScrollComponent;
	@ViewChild('submitEditsFooter', { static: false })
	public submitEditsFooter: StickyOnScrollComponent;

	@ViewChild('tabControl')
	public tabControl: SimpleTabUIComponent;
	@ViewChild('tabControl', { read: ElementRef })
	private tabControlElement: ElementRef;

	@ViewChild(JnJInformationUpdateComponent)
	public jnjInformation: JnJInformationUpdateComponent;
	@ViewChild(BlueKnightInformationUpdateComponent)
	public blueKnightInformation: BlueKnightInformationUpdateComponent;
	@ViewChild(BlueKnightMilestonesUpdateComponent)
	public blueKnightMilestones: BlueKnightMilestonesUpdateComponent;
	@ViewChild(PublicInformationUpdateComponent)
	public publicInformation: PublicInformationUpdateComponent;

	@ViewChild(AssetsUpdateComponent)
	public assets: AssetsUpdateComponent;

	private _isSubmitBarInvisible: boolean;
	private _routeParamsSubscription: Subscription;

	public currentTab: TabConfig = TabConfig.PUBLIC;

	public constructor(
		dc: DeploymentContext,
		private _route: ActivatedRoute,
		private _breadcrumbsService: BreadcrumbsService,
		private _reviewEditsService: ReviewEditsService,
		private userService: UserService,
		_companyService: CompanyService,
		_companyUpdateService: CompanyUpdateService,
		public _toastrService: ToastrService,
		private titleAndMetadata: TitleAndMetadata,
		private _webAnalyticsService: WebAnalyticsService
	) {
		super(dc, _companyUpdateService, _companyService);
		if (!this.isFollowEnabled) {
			this.isShareWithFollowers = false;
		}
	}

	public get isEditMode(): boolean {
		return this.selfUpdateMode === SelfUpdateMode.Edit;
	}

	public get isReviewMode(): boolean {
		return this.selfUpdateMode === SelfUpdateMode.Review;
	}

	public get progressUpdateString(): string {
		return this.updateService.progressUpdateString;
	}

	public get hasUpdatesToShareWithFollowers(): boolean {
		return this._reviewEditsService.editsApproved > 0;
	}

	public get isEditsSummaryInactive(): boolean {
		return !!this._reviewEditsService.currentEditItemProperty;
	}

	public get isFollowEnabled(): boolean {
		return this._deploymentContext.featureSwitches.enableFollowCompanies;
	}

	public get isReviewPendingProgressUpdate(): boolean {
		return this.isReviewMode && this.isProgressUpdateSet;
	}

	public get updateSubmitted(): boolean {
		return this.updateService.updateSubmitted;
	}

	public set updateSubmitted(value: boolean) {
		this.updateService.updateSubmitted = value;
	}

	public get progressUpdate(): string {
		return this.updateService.progressUpdate;
	}

	public set progressUpdate(value: string) {
		this.updateService.progressUpdate = value;
	}

	public get isEditingReviewComments(): boolean {
		return this.updateService.isEditingReviewComments;
	}

	public set isEditingReviewComments(value: boolean) {
		this.updateService.isEditingReviewComments = value;
	}

	public get reviewSubmitted(): boolean {
		return this.updateService.reviewSubmitted;
	}

	public set reviewSubmitted(value: boolean) {
		this.updateService.reviewSubmitted = value;
	}

	public get showFlag(): boolean {
		return (
			this.isProgressUpdateSet &&
			this._reviewEditsService.isPropertyInNeedOfReview(
				this.progressUpdateString
			)
		);
	}

	public get isShowingRevertProgressUpdate(): boolean {
		return (
			this.isReviewPendingProgressUpdate &&
			(this.isEditingProperty[this.progressUpdateString] ||
				this.company[this.progressUpdateString] !==
					this.companyWithPending[this.progressUpdateString])
		);
	}

	public get isSubmitEditsDisabled(): boolean {
		// Need to check validity to make sure there's at least company name
		if (
			!this.parentForm.valid ||
			this.company.assets?.some((asset) => hasRequiredChanges(asset))
		) {
			return true;
		}
		return (
			!this.hasChanges(this.companyWithPending) &&
			this.pendingUpdate?.status !== UpdateStatus.Saved
		);
	}

	public get missingWarning(): string {
		return this.LocPluralize(
			LocalizedTextIds.CompanyUpdateFieldsNeed,
			this.invalidFieldCount,
			this.invalidFieldCount
		);
	}

	public get missingFields(): string {
		return this.invalidFields
			.map((field: CompanyKey) => this.getHeaderForProperty(field))
			.join(', ');
	}

	public get isSaveEditsDisabled(): boolean {
		return (
			this.isEditingAnyProperty ||
			!this.hasChanges(this.companyWithPending)
		);
	}

	public hasChanges(compareTo: Company = this.companyClone): boolean {
		function areEqual(s1: string, s2: string): boolean {
			return (s1 || '') === (s2 || '');
		}

		if (this.isProgressUpdateSet) {
			if (!areEqual(this.progressUpdate, compareTo.progressUpdate)) {
				return true;
			}
		} else {
			if (
				this.progressUpdate?.trim() &&
				!areEqual(this.progressUpdate, compareTo.progressUpdate)
			) {
				return true;
			}
		}
		const diff: Diff = compareCompanies(this.company, compareTo);
		if (
			diff.hasPropertyChanges ||
			diff.hasDealChanges ||
			diff.hasFundingChanges ||
			diff.hasAssetChanges
		) {
			return true;
		}
		return false;
	}

	public get isSubmitReviewDisabled(): boolean {
		return (
			this._reviewEditsService.editsToReview > 0 ||
			(this.hasUpdatesToShareWithFollowers &&
				this.isShareWithFollowers === undefined)
		);
	}

	public get isSubmitBarInvisible(): boolean {
		const revs =
			this._reviewEditsService.editsToReview +
			this._reviewEditsService.totalEditsReviewed;
		const isInvisible =
			this._reviewEditsService.editsToReview > 0 || revs === 0;

		if (isInvisible !== this._isSubmitBarInvisible) {
			this._isSubmitBarInvisible = isInvisible;
		}

		return isInvisible;
	}

	public get submittedUpdateForConfirmation(): SubmittedUpdate {
		return this.updateService.submittedUpdate;
	}

	public get hideConfirmation(): boolean {
		return (
			(this.isEditMode && !this.updateSubmitted) ||
			(this.isReviewMode && !this.reviewSubmitted)
		);
	}

	public goToJlabsPage(): void {
		const destinationUrl = `${
			this._deploymentContext.hostingSiteUrl
		}/company/${urlIdentifierForCompany(
			this.companyBeforeAnyChanges.name
		)}`;
		window.open(destinationUrl, '_blank');
	}

	public get isShowingJnjInfo(): boolean {
		return this.currentTab === TabConfig.JNJ_ONLY;
	}

	public get isShowingPublicInfo(): boolean {
		return this.currentTab === TabConfig.PUBLIC;
	}

	public get isShowingAssets(): boolean {
		return this.currentTab === TabConfig.ASSETS;
	}

	public get isShowingBlueKnightInfo(): boolean {
		return this.currentTab === TabConfig.BLUE_KNIGHT;
	}

	public get isShowingBlueKnightMilestones(): boolean {
		return this.currentTab === TabConfig.BLUE_KNIGHT_MILESTONES;
	}

	public get isShowingAsset(): boolean {
		return this.currentTab === TabConfig.ASSET;
	}

	public itemEditDone(propertyName: string): void {
		if (isCompanyContactTitle(propertyName)) {
			this.companyWithMostRecentEdit.companyContact.title =
				this.company.companyContact.title;
		} else if (propertyName === 'firstTimeEntrepreneur') {
			this.companyWithMostRecentEdit.firstTimeEntrepreneur =
				this.company.firstTimeEntrepreneur;
			this.companyWithMostRecentEdit.minorityLed =
				this.company.minorityLed;
			this.companyWithMostRecentEdit.womenLed = this.company.womenLed;
		} else {
			this.companyWithMostRecentEdit[propertyName] = _.cloneDeep(
				this.company[propertyName]
			);
		}
		// NOTE: The company object is updated implicitly
		this.isEditingProperty[propertyName] = false;
		this._reviewEditsService.currentEditItemProperty = null;
	}

	public get isEditingAnyProperty(): boolean {
		for (const key in this.isEditingProperty) {
			if (
				Object.prototype.hasOwnProperty.call(
					this.isEditingProperty,
					key
				)
			) {
				if (this.isEditingProperty[key]) {
					return true;
				}
			}
		}

		return false;
	}

	public commitOpenEditItems(): void {
		for (const key in this.isEditingProperty) {
			if (
				Object.prototype.hasOwnProperty.call(
					this.isEditingProperty,
					key
				)
			) {
				if (this.isEditingProperty[key]) {
					this.itemEditDone(key);
				}
			}
		}
		this.updateService.checkUpdatesForUI();
	}

	public revertItemEdit(propertyName: string): void {
		const key = propertyName as CompanyKey;

		// If the item is a parent, revert its children
		const children = getDependentCompanyFields(key);
		const itemsToRevert = [propertyName, ...children];

		// Check if this item is dependent on another property (a child)
		const parent = getCompanyUpdateOptions(key)
			.preconditionProp as CompanyKey;

		// If we are changing a child but not the parent, leave the parent alone since
		// we are choosing from the same sets. If the parent is different, revert it and
		// revert its children
		if (
			itemIsDifferent(parent, this.company, this.companyBeforeAnyChanges)
		) {
			itemsToRevert.push(parent);
			itemsToRevert.push(...getDependentCompanyFields(parent));
		}

		itemsToRevert.forEach((item) =>
			this.updateService.revertItemEdit(item)
		);
		this.updateService.revertItemEdit(propertyName);
		// Reinitialize the edits form.
		this.initializeCompanyEditForm();
		this.setFormControlValues(this.company);
		this.updateService.checkUpdatesForUI();

		this._webAnalyticsService.trackEvent('company-update-field-reverted', {
			category: 'Company Update',
			label: `${propertyName} - ${this.company?.name}`,
		});
	}

	public consumeEvent(ev: UIEvent): void {
		ev.preventDefault();
		ev.stopPropagation();
	}

	public makePropertyEditable(
		propertyName: string,
		ev?: UIEvent,
		consumeEvent?: boolean
	): void {
		if (ev && consumeEvent) {
			this.consumeEvent(ev);
		}

		if (!this.isEditingProperty[propertyName]) {
			this.commitOpenEditItems();
			this.isEditingProperty[propertyName] = true;
			this._reviewEditsService.currentEditItemProperty = propertyName;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} else if ((ev?.target as any).name === 'edit-button') {
			this.itemEditDone(propertyName);
			return;
		}

		// Set focus in a timeout to give the UI a cycle to unhide any hidden edit items.
		setTimeout(() => {
			const element = document.getElementById(
				`company-update-${propertyName}`
			);
			if (element) {
				element.focus();
			}
		}, 300);
	}

	/*
	 * This is separate from makePropertyEditable(), above, because reviewComments
	 * is not technically an edit-item, so it shouldn't need to manage other edit
	 * items or tell anyone else when it's done. It also isn't being communicated
	 * across components, so it shouldn't need a UI cycle to unhide anything. It
	 * does, however, need to have its edit state managed by the click HostListener.
	 */
	public makeReviewCommentsEditable(ev: MouseEvent): void {
		this.consumeEvent(ev);
		this.isEditingReviewComments = true;
	}

	public ngOnDestroy(): void {
		this.unsubscribe();
		this._routeParamsSubscription?.unsubscribe();
	}

	public async ngOnInit(): Promise<void> {
		this.ngOnInitInternal();
		if (!this._routeParamsSubscription) {
			this._routeParamsSubscription = this._route.params.subscribe(() => {
				// FUTURE: Consider figuring out which things don't need to be reinitialized.
				// Don't double-init, because it will, at the very least duplicate some of our subscriptions below,
				// which would be a bad thing. If we have a _routeParamsSubscription, it means we already initialized.
				if (this._routeParamsSubscription) {
					this.ngOnInitInternal();
				}
			});
		}
	}

	private async ngOnInitInternal(): Promise<void> {
		this._reviewEditsService.clear();
		this.reviewSubmitted = false;
		this.updateSubmitted = false;

		this.updateService.initializeCompany(this._route.snapshot.data.company);

		if (this._deploymentContext.enabledModes.includes(Mode.Asset)) {
			this.tabs.push(this.assetsTab);
		}

		this.tabs.push(this.internalTab);

		if (
			this.company.isBlueKnight &&
			this.featureSwitches.enableBlueKnight
		) {
			this.tabs.push(this.blueKnightTab);
			this.tabs.push(this.blueKnightMilestonesTab);
		}

		if (this.userService.isReviewer) {
			this.selfUpdateMode = SelfUpdateMode.Review;
			this._breadcrumbsService.setDetailsBreadcrumb(
				'review-update',
				this.Loc(
					LocalizedTextIds.CompanyUpdateReviewEdits,
					this.company.name
				)
			);
		} else {
			this.selfUpdateMode = SelfUpdateMode.Edit;
		}

		this.initializeCompanyEditForm();

		// See if there is a pending update for this company, and if so, deal with it.
		await this.updateService.initializeUpdateData();

		this.setFormControlValues(this.company);

		// FUTURE - The code in these subscriptions should probably live in the CompanyUpdateService
		this.addSubscription(
			this.notificationsComponent.retractionSubject.subscribe(
				async () => {
					this.updateService.applyUpdateRetracted();
					this.setFormControlValues(
						_.merge({}, this.companyBeforeAnyChanges)
					);
					this.showPublicTab();
				}
			)
		);
		this.addSubscription(
			this.submissionConfirmation.nextReview.subscribe(async () => {
				this.isShareWithFollowers = this.isFollowEnabled
					? undefined
					: false;
			})
		);
		this.addSubscription(
			this.submissionConfirmation.returnToProfileSubject.subscribe(
				async () => {
					await this.notificationsComponent.getNotification();
					this.updateSubmitted = false;
					await this.updateService.initializeUpdateData();
					this.setFormControlValues(this.company);
				}
			)
		);
	}

	public ngAfterViewChecked(): void {
		this.AttachEditItemComponentSubscriptions();
	}

	private AttachEditItemComponentSubscriptions() {
		// We want to be sure we are looking at the edit components for the main page
		// (If there are any) and from each tab component
		const allEditItemComponents: QueryList<EditItemComponent> =
			new QueryList();
		allEditItemComponents.reset(
			[
				this.editItemArray,
				this.publicInformation?.editItemArray,
				this.jnjInformation?.editItemArray,
				this.blueKnightInformation?.editItemArray,
				this.blueKnightMilestones?.editItemArray,
			].filter(Boolean)
		);

		for (const editItemComponent of allEditItemComponents) {
			this.addSubscriptionHandlerIfNeeded(
				editItemComponent.makeEditableSubject,
				(payload: { propertyName: string; ev: MouseEvent }) => {
					this.makePropertyEditable(payload.propertyName, payload.ev);
				}
			);
			this.addSubscriptionHandlerIfNeeded(
				editItemComponent.revertSubject,
				(propertyName: string) => {
					this.revertItemEdit(propertyName);
				}
			);
			if (editItemComponent.type === EditItemType.MultiModal) {
				this.addSubscriptionHandlerIfNeeded(
					editItemComponent.itemReviewSubject,
					(itemReview: ItemReviewModification) => {
						this.updateService.applyItemReviewModification(
							itemReview,
							editItemComponent.propertyName as CompanyKey
						);
					}
				);
			}
		}
	}

	public changeTab(tab: TabInfo): void {
		this.currentTab = tab.value;
	}

	public showAssetTab() {
		this.changeTab({ value: TabConfig.ASSET });
	}

	public showPublicTab() {
		// Don't just call this.changeTab directly or the highlight won't change
		this.tabControl.changeTab(
			this.tabs.find((tab) => tab.value === TabConfig.PUBLIC)
		);
	}

	public showAssetsTab() {
		this.tabControl.changeTab(
			this.tabs.find((tab) => tab.value === TabConfig.ASSETS)
		);
	}

	@HostListener('window:click')
	protected onClick(): void {
		// Commit any open edit items
		this.commitOpenEditItems();
		// Address the review comments, which are not technically an edit item.
		this.isEditingReviewComments = false;
	}

	public async revertChanges(): Promise<void> {
		await this.updateService.revertChanges();
		// Reinitialize the edits form.
		this.initializeCompanyEditForm();
		this.setFormControlValues(this.company);
	}

	public get onLastTab(): boolean {
		if (
			this.company.isBlueKnight &&
			this.featureSwitches.enableBlueKnight
		) {
			return this.currentTab === TabConfig.BLUE_KNIGHT_MILESTONES;
		} else {
			return this.currentTab === TabConfig.JNJ_ONLY;
		}
	}

	public nextTab(): void {
		this.tabControl.changeTab(
			this.tabs.find((tab) => tab.value === this.nextTabConfig)
		);
		this._deploymentContext.scrollToElementTop(
			this.tabControlElement.nativeElement
		);
	}

	public nextTabOrSubmit(): void {
		if (this.onLastTab) {
			if (this.isEditMode) {
				this.updateCompany();
			} else if (this.isReviewMode) {
				this.submitReview();
			}
		} else {
			this.nextTab();
		}
	}

	private get nextTabConfig(): TabConfig {
		switch (this.currentTab) {
			case TabConfig.PUBLIC:
				return this._deploymentContext.enabledModes.includes(Mode.Asset)
					? TabConfig.ASSETS
					: TabConfig.JNJ_ONLY;
			case TabConfig.ASSET:
				return TabConfig.ASSETS;
			case TabConfig.ASSETS:
				return TabConfig.JNJ_ONLY;
			case TabConfig.JNJ_ONLY:
				return this.company.isBlueKnight &&
					this.featureSwitches.enableBlueKnight
					? TabConfig.BLUE_KNIGHT
					: TabConfig.PUBLIC;
			case TabConfig.BLUE_KNIGHT:
				return TabConfig.BLUE_KNIGHT_MILESTONES;
			case TabConfig.BLUE_KNIGHT_MILESTONES:
				return TabConfig.PUBLIC;
		}
	}

	public cancelReview(): void {
		this._reviewEditsService.cancel();
	}

	public async submitReview(): Promise<void> {
		this.updateService.submitReview();
		this.header.searchBar.refreshReviewNotifications();
		this._deploymentContext.ensureScrolledToTop();

		this._webAnalyticsService.trackEvent('company-update-reviewed', {
			category: 'Company Update',
			label: this.company?.name,
		});
	}

	public synchronizeProgressUpdate(progressUpdate: string): void {
		this.company.progressUpdate = progressUpdate;
	}

	public toggleTooltip(whichTooltip: string): void {
		this.isShowingTooltip[whichTooltip] =
			!this.isShowingTooltip[whichTooltip];
	}

	// NOTE: The progress update is handled differently than the rest of the updatable fields.
	//       A new progressUpdate is created in JForce any time the user supplies one, so we
	//       don't use the progressUpdate hanging off this.company as the ngModel, since that
	//       would display the value in the edit UI, which we don't want. We do, however, want
	//       to show a pending progressUpdate if a user returns here while an update is pending
	//       review.
	//       Note, also, that the UI/UX team says that a progressUpdate that has already been
	//       submitted can only be deleted by retracting the entire submission.
	public async updateCompany(): Promise<void> {
		// Commit any open edit items
		this.commitOpenEditItems();

		// Have the service update the data appropriately
		this.updateService.updateCompany();

		// Set the page title and return to the top of the page
		this.titleAndMetadata.setPageTitle(this.company.name);
		this._deploymentContext.ensureScrolledToTop();

		this._webAnalyticsService.trackEvent('company-update-submitted', {
			category: 'Company Update',
			label: this.company?.name,
		});
	}

	public async saveEdits(): Promise<void> {
		// Commit any open edit items
		this.commitOpenEditItems();

		await this.updateService.saveCompany(false);
		await this.notificationsComponent.getNotification();

		this._webAnalyticsService.trackEvent('company-update-draft-saved', {
			category: 'Company Update',
			label: this.company?.name,
		});
	}

	private initializeCompanyEditForm(): void {
		this.companyEditFormSubscription?.unsubscribe();

		// We need to manually create controls for any fields that do not directly exist on the company (e.g. contact fields)
		const controls = {
			contactName: this.createDisabledControl(),
			companyContactTitle: new UntypedFormControl(),
		};

		for (const updatableField of getUpdateableCompanyFields().filter(
			(field) => !this.isFieldDisabled(field)
		)) {
			const updateOptions = getCompanyUpdateOptions(updatableField);
			const isBlueKnight = isBlueKnightProperty(updatableField);

			if (updateOptions.required) {
				controls[updatableField] = isBlueKnight
					? this.createBlueKnightRequiredControl()
					: this.createMoreThanSpacesRequiredControl();
			} else {
				controls[updatableField] = new UntypedFormControl();
			}
		}

		this.parentForm = new UntypedFormGroup(controls);

		this.companyEditFormSubscription =
			this.parentForm.valueChanges.subscribe((formValues) => {
				if (
					formValues.companyContactTitle &&
					this.company.companyContact
				) {
					formValues.companyContact = {
						name: this.parentForm.get('contactName')?.value,
						title: formValues.companyContactTitle,
					};
				}

				this.company = {
					...this.company,
					...formValues,
				};

				// Do not allow the flattened property to "bleed through"
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				delete (this.company as any).companyContactTitle;
			});
	}

	private isFieldDisabled(field: CompanyKey): boolean {
		switch (field) {
			case 'primarySector':
			case 'primarySubSector':
			case 'secondarySector':
			case 'secondarySubSector':
				return !this.featureSwitches.companyUpdateSwitches
					.enableSectorUpdates;
			case 'problem':
			case 'solution':
				return !this.featureSwitches.companyUpdateSwitches
					.enableProblemAndSolution;
			case 'primaryIndication':
				return !this.featureSwitches.enableIndicationUpdates;
			case 'keyMgmtAndAdvBm':
				return !this.featureSwitches.companyUpdateSwitches
					.enableKeyManagement;
			case 'firstTimeEntrepreneur':
				return !this.featureSwitches.companyUpdateSwitches
					.enableFirstTimeEntrepreneur;
			case 'tags':
				return !this.featureSwitches.companyUpdateSwitches.enableTags;
		}

		if (
			isBlueKnightProperty(field) &&
			!this.featureSwitches.enableBlueKnight
		) {
			return true;
		}

		return false;
	}

	public get includeProgressUpdate(): boolean {
		return this.featureSwitches.companyUpdateSwitches.enableProgressUpdates;
	}

	public createBlueKnightRequiredControl(): UntypedFormControl {
		return new UntypedFormControl(
			'',
			requireIfBlueKnightCompany(this.company)
		);
	}

	public createMoreThanSpacesRequiredControl(): UntypedFormControl {
		return new UntypedFormControl('', RequireMoreThanJustSpaces.required);
	}

	public createRequiredControl(): UntypedFormControl {
		return new UntypedFormControl('', Validators.required);
	}

	public createDisabledControl(): UntypedFormControl {
		return new UntypedFormControl({ value: '', disabled: true });
	}

	public formControlValuesSet = false;

	private setFormControlValues(company: Company): void {
		// FUTURE: Something strange is going on, where if I access the company object directly,
		//         fields are getting emptied out, hence the workaround below. Figure out what's
		//         happening, and see if we can avoid this clone.
		const clone = _.cloneDeep(company);
		for (const key in clone) {
			if (Object.prototype.hasOwnProperty.call(clone, key)) {
				const val = clone[key];
				if (key === 'companyContact') {
					if (val != null) {
						let fc = this.parentForm.get('contactName');
						fc.setValue(val.name);
						fc = this.parentForm.get('companyContactTitle');
						fc.setValue(val.title);
					}
				} else {
					const fc = this.parentForm.get(key);
					if (fc) {
						let typeConvertedVal = val;
						if (typeof val === 'boolean') {
							typeConvertedVal = val.toString();
						}
						fc.setValue(typeConvertedVal);
					}
				}
			}
		}
		this.formControlValuesSet = true;

		// Re-attach subscriptions, in case there are newly visible controls
		this.AttachEditItemComponentSubscriptions();
	}

	private get invalidFields(): string[] {
		const fieldNames: string[] = [];
		if (this.formControlValuesSet) {
			const controls = this.parentForm.controls;
			Object.keys(controls).forEach((key) => {
				const control = controls[key];
				if (control.errors) {
					fieldNames.push(key);
				}
			});

			this.company.assets?.forEach((asset) => {
				if (hasRequiredChanges(asset)) {
					fieldNames.push(
						`${this.Loc(LocalizedTextIds.Asset)} -  ${
							asset.name ?? 'Unnamed Asset'
						}`
					);
				}
			});
		}
		return fieldNames;
	}

	public get invalidFieldCount(): number {
		return this.invalidFields.length;
	}

	public getErrorsForTab(cmpnName: string): string[] {
		const cmpn: UpdateComponentBaseWithEditItems = this[cmpnName];
		if (!cmpn) {
			return [];
		}
		const fieldNames = this.invalidFields;
		const fieldsOnThisTab = cmpn.editItemComponents
			.filter((item) => fieldNames.includes(item.propertyName))
			.map((editItem) => editItem.labelText);
		return fieldsOnThisTab;
	}

	public get nextTabOrSubmitButtonTitle(): string {
		if (this.onLastTab && this.isSubmitEditsDisabled) {
			return this.Loc(LocalizedTextIds.CompanyUpdateTheSubmitEdits);
		}
		return '';
	}

	public handleDisabledSubmitClick(): void {
		// If Submit button disabled but gets clicked, give a message
		const message = this.nextTabOrSubmitButtonTitle;
		if (message) {
			const title = this.Loc(
				LocalizedTextIds.CompanyUpdateCommandDisabled
			);
			this._toastrService.warning(message, title, {
				positionClass: 'toast-bottom-right',
				timeOut: 5000,
			});
		}
	}
}
